import React, { useState, useEffect } from 'react';
import { ListItemFormData } from '../../types';
import './lists.css';

interface ListItemFormProps {
  initialData?: Partial<ListItemFormData>;
  onSubmit: (data: ListItemFormData | Partial<ListItemFormData>) => Promise<void>;
  onCancel: () => void;
}

const ListItemForm: React.FC<ListItemFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ListItemFormData>({
    name: initialData?.name || '',
    description: initialData?.description || undefined,
    link: initialData?.link || undefined,
    price: initialData?.price ?? undefined,
  });
  const [priceInput, setPriceInput] = useState<string>(
    initialData?.price != null ? initialData.price.toString() : ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update form data when initialData changes
  useEffect(() => {
    setFormData({
      name: initialData?.name || '',
      description: initialData?.description || undefined,
      link: initialData?.link || undefined,
      price: initialData?.price ?? undefined,
    });
    setPriceInput(
      initialData?.price != null ? initialData.price.toString() : ''
    );
    setErrors({});
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Il nome è obbligatorio';
    }

    if (formData.link && !isValidUrl(formData.link)) {
      newErrors.link = 'Inserisci un URL valido';
    }

    if (formData.price !== undefined) {
      if (Number.isNaN(formData.price)) {
        newErrors.price = 'Il prezzo deve essere un numero valido';
      } else if (formData.price < 0) {
        newErrors.price = 'Il prezzo non può essere negativo';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Clean up the data before submitting
      const submitData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        link: formData.link?.trim() || undefined,
        price: formData.price,
      };

      await onSubmit(submitData);
    } catch (err) {
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof ListItemFormData, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handlePriceInput = (value: string) => {
    // Permetti solo numeri, virgole, punti e al massimo 2 decimali
    const numericPattern = /^(\d*[.,]?\d{0,2})$/;

    if (!numericPattern.test(value) && value !== '') {
      return;
    }

    const normalized = value.replace(',', '.');

    if (value === '') {
      setPriceInput('');
      handleChange('price', undefined);
      return;
    }

    const parsed = Number.parseFloat(normalized);

    setPriceInput(value);
    handleChange('price', Number.isNaN(parsed) ? undefined : parsed);
  };

  const handlePriceBlur = () => {
    // Stabilizza il valore quando il campo perde il focus
    if (priceInput && formData.price !== undefined && !Number.isNaN(formData.price)) {
      // Formatta il valore con 2 decimali se è un numero valido
      const formattedValue = formData.price.toFixed(2);
      setPriceInput(formattedValue);
    } else if (priceInput && formData.price === undefined) {
      // Se il parsing ha fallito ma c'è ancora del testo, prova a ripulire
      const cleaned = priceInput.replace(/[^\d.,]/g, '').replace(',', '.');
      const parsed = Number.parseFloat(cleaned);
      
      if (!Number.isNaN(parsed) && parsed >= 0) {
        setPriceInput(parsed.toFixed(2));
        handleChange('price', parsed);
      } else {
        // Se non è possibile parsare, svuota il campo
        setPriceInput('');
        handleChange('price', undefined);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="list-item-form">
      <div className="form-group">
        <label htmlFor="name">Nome *</label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          className={errors.name ? 'error' : ''}
          placeholder="Es. iPhone 15, Libro di cucina..."
          disabled={isSubmitting}
        />
        {errors.name && <span className="error-text">{errors.name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="description">Descrizione</label>
        <textarea
          id="description"
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Aggiungi dettagli, colore, taglia, modello..."
          rows={3}
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="link">Link prodotto</label>
        <input
          type="url"
          id="link"
          value={formData.link || ''}
          onChange={(e) => handleChange('link', e.target.value)}
          className={errors.link ? 'error' : ''}
          placeholder="https://..."
          disabled={isSubmitting}
        />
        {errors.link && <span className="error-text">{errors.link}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="price">Prezzo (€)</label>
        <input
          type="text"
          inputMode="decimal"
          id="price"
          value={priceInput}
          onChange={(e) => handlePriceInput(e.target.value)}
          onBlur={handlePriceBlur}
          className={errors.price ? 'error' : ''}
          placeholder="0.00"
          disabled={isSubmitting}
        />
        {errors.price && <span className="error-text">{errors.price}</span>}
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="cancel-button"
          disabled={isSubmitting}
        >
          Annulla
        </button>
        <button
          type="submit"
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Salvataggio...' : (initialData ? 'Aggiorna' : 'Aggiungi')}
        </button>
      </div>
    </form>
  );
};

export default ListItemForm;
